import { useEffect } from 'react';
import { useNow } from '../../../../../hooks/useNow';
import {
    aiReplyState,
    type AiReplySource,
    type AiReplyState
} from '../../../../monitoring/messages/triage/aiTriageState';
import type { DmComposerHandle } from '../composer/DmMessageComposer';
import { AiDraftBubble } from './AiDraftBubble';
import { AiDraftNote } from './AiDraftNote';
import { aiReplyStateKey } from './aiThreadDraft';

// Fine enough for the 10-minute "not triaged" threshold; only this slot re-renders on a tick.
const CLOCK_TICK_MS = 30_000;

type NoteKind = Exclude<AiReplyState['kind'], 'draft' | 'none'>;

const NOTE_TEXT: Record<NoteKind, string> = {
    noReplyNeeded: 'No reply needed',
    draftUnavailable: 'Draft unavailable',
    notTriaged: 'Not triaged by AI yet'
};

interface AiReplyInThreadProps {
    source: AiReplySource;
    composer: DmComposerHandle;
    // Called after what this slot shows changes, so the thread can keep its end in view.
    onContentChange: () => void;
}

// The AI's take on the newest patient message, shown after it; renders nothing for 'none'.
export function AiReplyInThread({ source, composer, onContentChange }: AiReplyInThreadProps) {
    const now = useNow(CLOCK_TICK_MS);
    const state = aiReplyState({ ...source, now });
    const contentKey = aiReplyStateKey(state);

    useEffect(() => {
        onContentChange();
    }, [contentKey, onContentChange]);

    if (state.kind === 'none') return null;
    if (state.kind === 'draft') {
        // Keyed by the text so a pending replace confirm never applies to a newer draft.
        return (
            <AiDraftBubble
                key={state.reply}
                draft={state}
                generatedAt={source.triage.suggestedReplyGeneratedAt}
                composer={composer}
            />
        );
    }
    return <AiDraftNote text={NOTE_TEXT[state.kind]} />;
}
